import { omit, get } from 'lodash';
import { BAD_REQUEST } from 'http-status-codes';
import {
	INIT_VALIDATION,
	UPDATE_VALIDATION,
	HIDE_ERROR_MSG,
	SHOW_ALL_ERRORS,
	INIT_VALIDATION_FOR_TEMPLATE,
	SET_TYPE,
	REMOVE_KEY,
	ADD_VALIDATION_KEY,
	INIT_VALIDATION_FOR_ADD_PAYEE,
	INIT_VALIDATION_FOR_EDIT_PAYEE,
	INIT_VALIDATION_FOR_EDIT_PAYEE_CHAPS,
	INIT_VALIDATION_FOR_ADD_INTERNATIONAL_PAYEE,
	DELETE_VALIDATION_FIELD,
	INIT_VALIDATION_FOR_IFT_PAYMENT_DETAILS,
} from './actions';
import { SUBMIT_ADD_PAYEE_FAILURE, SUBMIT_ADD_INTERNATIONAL_PAYEE_FAILURE } from '../../payees/states/addPayee/actions';
import { BacsHandlers, defaultState as bacsDefaultRowState } from './bacs';
import { FPHandlers, defaultItem, defaultTemplateItem } from './fasterpayments';
import { TYPE_FASTER_PAYMENTS, TYPE_BACS_CREDIT, TYPE_BACS_DEBIT, TYPE_IFT } from '../../config';

import {
	initialState,
	defaultAddPayeeItem,
	defaultEditPayeeItem,
	defaultEditPayeeChapsItem,
	defaultAddInternationalPayeeItem,
	defaultIFTPaymetDetailsItem,
	paymentsFlowAddPayeeItems,
	paymentsFlowAddInternationalPayeeItems,
	formSequence,
} from './common';
import { backEndErrorStates } from './backEndErrors';
import { IFTHandlers } from './iftPayments';
import { RESET_APP } from '../page/actions';

const getDefaultItem = (paymentType, templateMode = false) => {
	switch (paymentType) {
		case TYPE_BACS_DEBIT:
		case TYPE_BACS_CREDIT:
			return bacsDefaultRowState;
		default:
			return templateMode ? defaultTemplateItem : defaultItem;
	}
};

const checkForBackEndErrors = (state, action, addPayeeKey) => {
	const {
		payload: { response = {}, status },
	} = action;
	const { coded_exceptions = [] } = response;
	if (status === BAD_REQUEST) {
		return backEndErrorStates(coded_exceptions, state, addPayeeKey);
	}
	return state;
};

const handlers = {
	[SET_TYPE]: (state, action) => ({ ...state, paymentType: action.paymentType }),
	[INIT_VALIDATION]: (state, action) => ({
		...state,
		paymentType: action.paymentType,
		[action.id]: { ...getDefaultItem(state.paymentType, action.templateMode) },
	}),
	[INIT_VALIDATION_FOR_TEMPLATE]: (state) => ({
		...state,
		'template-name': { name: { ...initialState, isValid: true } },
	}),
	[INIT_VALIDATION_FOR_IFT_PAYMENT_DETAILS]: (state) => ({
		...state,
		iftPaymentDetails: { ...defaultIFTPaymetDetailsItem },
	}),
	[INIT_VALIDATION_FOR_ADD_PAYEE]: (state, action) => {
		const defaultItems = action.isPaymentFlow ? paymentsFlowAddPayeeItems : defaultAddPayeeItem;
		if (action.isFromChapsPayment) {
			delete defaultItems.reference;
		}
		return {
			...state,
			addPayee: { ...defaultItems },
		};
	},
	[INIT_VALIDATION_FOR_EDIT_PAYEE]: (state, action) => ({
		...state,
		[action.id]: { ...defaultEditPayeeItem(action.payeeDetails) },
	}),
	[INIT_VALIDATION_FOR_EDIT_PAYEE_CHAPS]: (state, action) => ({
		...state,
		[action.id]: { ...defaultEditPayeeChapsItem },
	}),
	[INIT_VALIDATION_FOR_ADD_INTERNATIONAL_PAYEE]: (state, action) => {
		const defaultItems = action.isPaymentFlow
			? paymentsFlowAddInternationalPayeeItems
			: defaultAddInternationalPayeeItem;
		return {
			...state,
			addInternationalPayee: { ...defaultItems },
		};
	},
	[UPDATE_VALIDATION]: (state, action) => ({
		...state,
		[action.id]: {
			...state[action.id],
			[action.key]: {
				...state[[action.id]][action.key],
				isValid: action.isValid,
				isFocusOn: action.isFocusOn || false,
				isBackEndError: action.isBackEndError || false,
				showValidation: action.showValidation || false,
			},
		},
	}),
	[HIDE_ERROR_MSG]: (state, action) => ({
		...state,
		[action.id]: {
			...state[action.id],
			[action.key]: {
				...state[[action.id]][action.key],
				showValidation: false,
			},
		},
	}),
	[SHOW_ALL_ERRORS]: (state, { id }) => {
		const keys = Object.keys(omit(state, 'paymentType'));
		const newState = {};
		let isFocusOn = false;
		const showErrors = (key) => {
			const sequenceOrder = get(formSequence, key, undefined);
			const itemKeys = sequenceOrder || Object.keys(state[key]);
			newState[key] = {};
			itemKeys.forEach((itemKey) => {
				if (state[key][itemKey]) {
					newState[key][itemKey] = { ...state[key][itemKey], showValidation: true };
					if (!isFocusOn && !state[key][itemKey].isValid) {
						isFocusOn = true;
						newState[key][itemKey] = { ...newState[key][itemKey], isFocusOn };
					}
				}
			});
		};
		if (id) {
			showErrors(id);
			return { ...state, ...newState };
		}
		keys.forEach((key) => {
			showErrors(key);
		});
		return { ...state, ...newState };
	},
	[REMOVE_KEY]: (state, action) => {
		const { [action.id]: keyToDelete, ...newState } = state;
		return newState;
	},
	[DELETE_VALIDATION_FIELD]: (state, { id, fieldId }) => ({
		...state,
		[id]: omit(state[id], fieldId),
	}),
	[ADD_VALIDATION_KEY]: (state, action) => ({ ...state, [action.key]: action.value }),
	[SUBMIT_ADD_INTERNATIONAL_PAYEE_FAILURE]: (state, action) => checkForBackEndErrors(state, action, action.meta.id),
	[SUBMIT_ADD_PAYEE_FAILURE]: (state, action) => checkForBackEndErrors(state, action, action.meta.id),
	[RESET_APP]: () => initialState,
};
const validation = (state = {}, action = {}) => {
	if (
		(state.paymentType === TYPE_BACS_CREDIT || state.paymentType === TYPE_BACS_DEBIT) &&
		Object.keys(BacsHandlers).includes(action.type)
	) {
		return BacsHandlers[action.type](state, action);
	}
	if (state.paymentType === TYPE_FASTER_PAYMENTS && Object.keys(FPHandlers).includes(action.type)) {
		return FPHandlers[action.type](state, action);
	}
	if (state.paymentType === TYPE_IFT && Object.keys(IFTHandlers).includes(action.type)) {
		return IFTHandlers[action.type](state, action);
	}
	if (Object.keys(handlers).includes(action.type)) {
		return handlers[action.type](state, action);
	}
	return state;
};
export default validation;
